/**
 * Data model for the Group/Overall dashboard's "Net Revenue (monthly)" chart
 * (Fan, 2026-08-30: "Add a net revenue chart to the group dashboard (this is
 * after OTA commission and for BR OS, minus the COGS / supplier payment)").
 *
 * Definition, per segment -- deliberately NOT the same subtraction on both
 * sides, because the two businesses earn differently:
 *   - Property Portfolio: booking volume MINUS OTA/channel commission, i.e.
 *     portfolioDerived.js's own `net_revenue` (`revenue`) figure -- the same
 *     number the Property Portfolio dashboard's Net Revenue KPI shows.
 *   - BR OS: turnover MINUS the supplier cost of the booking (COGS), i.e.
 *     the gross margin the BR OS dashboard already computes with the
 *     booking's own locked FX rate. BR OS pays no OTA commission; the
 *     supplier payment IS its cost of sale.
 *
 * Companion to lib/groupGrossRevenueChart.js (booking volume) and
 * lib/groupNetRevenueChart is read by the same GroupOverviewTab. Both take
 * the SAME input object, so the two charts can never disagree about which
 * month or which currency they are describing.
 *
 * Forecast: the 3 months after the actuals boundary, on BOTH segments --
 * Portfolio from /reports/forecast's `revenue_gbp` (forward bookings, net of
 * commission) and BR OS from /reports/bros/dashboard's `forecast_months`
 * `gross_margin` (contracted nights, services/bros_forward_revenue.py).
 * Forecast values live on `<segment>_forecast` keys and are never summed
 * into the actuals bars or the actuals total.
 */

import { getActualsForecastBoundary } from './forecastChartUtils';

export var NET_REVENUE_SEGMENTS = [
  { key: 'br_os', label: 'BR OS', color: '#3B82F6' },
  { key: 'property_portfolio', label: 'Portfolio', color: '#10B981' },
];

function byMonthKey(list) {
  var m = {};
  (list || []).forEach(function (row) { if (row && row.month) m[row.month] = row; });
  return m;
}

/**
 * @param {object} input
 * @param {number} input.year
 * @param {{uk: object[], fr: object[]}} input.ppMonthlyByRegion
 * @param {object[]} input.brosMonths - /reports/bros/dashboard months (actuals)
 * @param {object[]} input.brosForecastMonths - /reports/bros/dashboard forecast_months
 * @param {object[]} input.ppForecastMonthly - /reports/forecast monthly
 * @param {string[]} input.months - short month labels
 * @param {Date} [input.refDate]
 */
export function buildNetRevenueChartData(input) {
  var year = input.year;
  var ppMonthlyByRegion = input.ppMonthlyByRegion || { uk: [], fr: [] };
  var brosByMonth = byMonthKey(input.brosMonths);
  var brosForecastByMonth = byMonthKey(input.brosForecastMonths);
  var ppForecastByMonth = byMonthKey(input.ppForecastMonthly);
  var months = input.months;
  var boundary = getActualsForecastBoundary(input.refDate);

  var rows = [];
  for (var i = 0; i < 12; i++) {
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    var uk = (ppMonthlyByRegion.uk || [])[i] || {};
    var fr = (ppMonthlyByRegion.fr || [])[i] || {};
    // Actuals-only, same standing rule as the booking-volume chart: a
    // same-month forward-dated booking is never an actual.
    // Net revenue (A148): after OTA, management fees, cleaning and repairs
    // (portfolioDerived's net_revenue_*); older rows without it fall back to
    // the after-OTA revenue.
    var hasTier = uk.net_revenue_gbp != null || fr.net_revenue_gbp != null;
    var hasSplit = hasTier
      ? (uk.net_revenue_actual_gbp != null || fr.net_revenue_actual_gbp != null)
      : (uk.revenue_actual_gbp != null || fr.revenue_actual_gbp != null);
    var ppNet = hasTier
      ? (hasSplit
        ? (uk.net_revenue_actual_gbp || 0) + (fr.net_revenue_actual_gbp || 0)
        : (uk.net_revenue_gbp || 0) + (fr.net_revenue_gbp || 0))
      : (hasSplit
        ? (uk.revenue_actual_gbp || 0) + (fr.revenue_actual_gbp || 0)
        : (uk.revenue_gbp || 0) + (fr.revenue_gbp || 0));

    var bros = brosByMonth[mk] || {};
    // BR OS net revenue = turnover - COGS. gross_margin is exactly that, and
    // is already currency-correct (BR OS's locked supplier rate).
    var brosNet = parseFloat(bros.gross_margin || 0);

    var row = {
      month: months[i],
      _monthKey: mk,
      'BR OS': brosNet,
      Portfolio: ppNet,
      _ppForward: hasSplit ? (uk.revenue_forward_gbp || 0) + (fr.revenue_forward_gbp || 0) : 0,
    };
    row._total = row['BR OS'] + row.Portfolio;
    row._carrier = row.Portfolio > 0 ? 'property_portfolio' : (row['BR OS'] > 0 ? 'br_os' : null);
    rows.push(row);
  }

  rows.forEach(function (row) {
    if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
    var ppFc = ppForecastByMonth[row._monthKey];
    var brosFc = brosForecastByMonth[row._monthKey];
    // Current month: Portfolio remainder = its forward-dated bookings (the
    // actual bar holds check-ins to date only). Later months: forward floor.
    if (row._monthKey === boundary.currentMonthKey) {
      if (row._ppForward > 0) {
        row.Portfolio_forecast = row._ppForward;
        row._isForecast = true;
      }
    } else if (ppFc && !row.Portfolio) {
      row.Portfolio_forecast = ppFc.revenue_gbp || 0;
      row._isForecast = true;
    }
    // BR OS forecast never overlaps its actuals (uninvoiced share of live
    // stays + invoices due later in the window), so it stacks on the current
    // month's actual bar as well.
    if (brosFc && parseFloat(brosFc.gross_margin || 0) !== 0) {
      row['BR OS_forecast'] = parseFloat(brosFc.gross_margin || 0);
      row._isForecast = true;
    }
  });

  return rows;
}

/** YTD totals per segment plus the combined figure (actuals only). */
export function computeNetRevenueYtd(chartData) {
  var out = { _combined: 0 };
  NET_REVENUE_SEGMENTS.forEach(function (seg) { out[seg.label] = 0; });
  (chartData || []).forEach(function (row) {
    NET_REVENUE_SEGMENTS.forEach(function (seg) {
      var v = row[seg.label] || 0;
      out[seg.label] += v;
      out._combined += v;
    });
  });
  return out;
}
